import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ModalController } from '@ionic/angular';

import { LinkService } from 'src/app/services/general/link.service';
import { LoginService } from 'src/app/services/general/login.service';
import { AuthService } from 'src/app/services/user/auth.service';
import { UserService } from 'src/app/services/user/user.service';
import { PopupService } from 'src/app/services/general/popup.service';

import { AccountsSetupComponent } from 'src/app/components/accounts/accounts-setup/accounts-setup.component';
import { ProductAccountDetailComponent } from 'src/app/components/subscription/product-account-detail/product-account-detail.component';

import { Credit } from 'src/app/interfaces/credit';

@Component({
  selector: 'app-gift-card',
  templateUrl: './gift-card.page.html',
  styleUrls: ['./gift-card.page.scss'],
})
export class GiftCardPage implements OnInit, OnDestroy {

  /**
   * Credit
   */
  credit: Credit;

  /**
   * Constructor
   * @param route route
   * @param router router
   * @param modalController modal controller
   * @param linkService link service
   * @param loginService login service
   * @param authService auth service
   * @param userService user service
   */
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private modalController: ModalController,
    private linkService: LinkService,
    private loginService: LoginService,
    private authService: AuthService,
    private userService: UserService,
    private popupService: PopupService,
  ) { }

  /**
   * On Init
   */
  ngOnInit() {
    this.watchRoute();
  }

  ngOnDestroy(): void {
  }

  /**
   * Watch route
   */
   watchRoute() {
    this.route.params.subscribe((param) => {
      const route = this.router.getCurrentNavigation();
      if (route?.extras?.state) {
        const state = route.extras.state;
        if (state.credit) {
          this.credit = state.credit;
          if (!this.credit.giftCardId) {
            this.credit.giftCardId = 'en-1';
          }
        }
      }
      if (!this.credit) {
        this.goMainPage();
      }
    });
  }

  /**
   * Redeem gift card
   */
  redeemGiftCard() {
    this.checkUserLogin();
  }
  
  /**
   * Check user login
   */
   async checkUserLogin() {
    await this.popupService.presentLoading();
    if (this.authService.currentUser) {
      if (this.userService.user?.name) {
        this.checkAnonymousLogin();
      } else {
        await this.popupService.dismissLoading();
        const modal = await this.loginService.presentUserSetupModal();
        modal.onDidDismiss().then(() => {
          this.checkAnonymousLogin();
          this.loginService.userSetupModal = null;
        });
      }
    } else {
      this.presentLoginModal(false, false);
    }
  }

  /**
   * Check anonymous login
   */
  async checkAnonymousLogin() {
    if (this.userService?.user?.isAnonymous) {
      this.presentLoginModal(false, false, true);
    } else {
      this.presentAccountSetupModal(this.credit.creditId);
    }
  }

  /**
   * Present login modal
   * @param loginRequired login required
   * @param showAnonymousLogin show anonymous login
   * @param linkProvider link provider
   */
  async presentLoginModal(loginRequired?: boolean, showAnonymousLogin?: boolean, linkProvider?: boolean) {
    await this.popupService.dismissLoading();
    const modal = await this.loginService.presentLoginModal(loginRequired, showAnonymousLogin, linkProvider);
    modal.onDidDismiss().then(async () => {
      if (this.userService.user && (showAnonymousLogin || !this.userService?.user?.isAnonymous)) {
        this.presentAccountSetupModal(this.credit.creditId);
      }
    });
  }

  /**
   * Present account setup modal
   * @param creditId credit id
   */
  async presentAccountSetupModal(creditId: string) {
    await this.popupService.dismissLoading();
    if (creditId) {
      const modal = await this.modalController.create({
        component: AccountsSetupComponent,
        componentProps: {
          premium: true,
          gift: true,
          creditId
        }
      });
      modal.present();
    } else {
      this.popupService.presentActionError();
    }
  }

  /**
   * Present product account detail modal
   * @param productId product id
   */
  async presentProductAccountDetailModal(productId?: string) {
    const modal = await this.modalController.create({
      component: ProductAccountDetailComponent,
      componentProps: {
        productId: productId ? productId : '',
      }
    });
    modal.present();
  }

  /**
   * Go main page
   */
  goMainPage() {
    this.linkService.goMainPage();
  }

}
